import React, { memo, useState } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import Animated, { FadeIn, runOnJS, useAnimatedReaction } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Freeze } from '@/components/Freeze';
import { Box } from '@/components/lib/components';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { SHEET_BORDER_RADIUS } from '@/feature/circular-pager/CircularPageSlot';
import { useFantasyFullScreenPagerTransition } from '@/feature/circular-pager/FantasyFullScreenPagerTransitionProvider';
import { useScrollableModalContext } from '@/feature/circular-pager/ScrollableModalContextProvider';
import { ListEmptyComponent } from '@/feature/fantasy-event-sheets/EventDetailsScreen';
import { PICK_BAR_HEIGHT } from '@/feature/lobby/components/Pickbar';
import { PlayerProfileHeader } from '@/feature/player-profile-sheets/components/PlayerProfileHeader';
import { common } from '@/styles/styles';
import { showScrollIndicator } from '@/utils/constants-platform-specific';
import { isWeb } from '@/utils/constants-platform-specific';
import { BottomSheetScrollView } from '@gorhom/bottom-sheet';

import { PLAYER_CAROUSEL_TAB_BAR_HEIGHT } from '../PlayerProfileSheets';
import { ProjectionsList } from '../ProjectionsList';
import { PlayerProfileSheetHandle } from './PlayerProfileSheetHandle';

const ScrollContainer = isWeb ? ScrollView : BottomSheetScrollView;

export const PlayerProfileSheet = ({
    shouldFreeze,
    playerInfo,
    isActive,
}: {
    shouldFreeze: boolean;
    playerInfo: PlayerWithTeamAndEvent;
    isActive: boolean;
}) => {
    return (
        <Freeze freeze={shouldFreeze} placeholder={<ListEmptyComponent />}>
            <PlayerProfileSheetScreen playerInfo={playerInfo} isActive={isActive} />
        </Freeze>
    );
};

export const PlayerProfileSheetScreen = memo(
    ({
        playerInfo,
        closeSinglePlayerProfile,
        isActive,
    }: {
        playerInfo: PlayerWithTeamAndEvent;
        closeSinglePlayerProfile?: () => void;
        isActive: boolean;
    }) => {
        const insets = useSafeAreaInsets();
        const { scrollOffset, sheetScrollableHook } = useScrollableModalContext();

        // on web, the player carousel is sticky so we need to add the padding for it
        const paddingBottom =
            insets.bottom + insets.top + (isWeb ? PLAYER_CAROUSEL_TAB_BAR_HEIGHT : 0) + PICK_BAR_HEIGHT;

        const [shouldRenderProjectionList, setShouldRenderProjectionList] = useState(false);
        const { sheetMountAnimationProgress } = useFantasyFullScreenPagerTransition();

        useAnimatedReaction(
            () => sheetMountAnimationProgress.value,
            current => {
                if (current !== null) {
                    //we don't show the projection list when the screen transition/mount starts, for better performance
                    //we only start showing the projection list after 70% of the mount animation is completed
                    if (sheetMountAnimationProgress.value > -0.7 && !shouldRenderProjectionList) {
                        runOnJS(setShouldRenderProjectionList)(true);
                    }
                }
            }
        );

        return (
            <Box flex={1} minHeight={'100%'} backgroundColor={'gray8'} style={styles.bottomSheetContainer}>
                <PlayerProfileSheetHandle playerInfo={playerInfo} />
                <ScrollContainer
                    contentContainerStyle={[{ paddingBottom }]}
                    showsVerticalScrollIndicator={showScrollIndicator}
                    scrollEventsHandlersHook={sheetScrollableHook}
                    style={styles.bottomSheetScrollContainer}
                >
                    <PlayerProfileHeader
                        scrollOffset={scrollOffset}
                        playerInfo={playerInfo}
                        closeSinglePlayerProfile={closeSinglePlayerProfile}
                    />
                    <Freeze freeze={!shouldRenderProjectionList || !isActive} placeholder={null}>
                        <Animated.View entering={FadeIn.duration(100)} style={common.zIndexNegative1}>
                            <Box px={'s16'} paddingTop={'s16'} height={'100%'} paddingBottom={'s24'}>
                                <ProjectionsList player={playerInfo} />
                            </Box>
                        </Animated.View>
                    </Freeze>
                </ScrollContainer>
            </Box>
        );
    }
);

const styles = StyleSheet.create({
    bottomSheetContainer: {
        overflow: 'hidden',
        borderTopLeftRadius: SHEET_BORDER_RADIUS,
        borderTopRightRadius: SHEET_BORDER_RADIUS,
    },
    bottomSheetScrollContainer: {
        minHeight: '100%',
    },
});
